import { AlertTriangle, CalendarClock } from "lucide-react";
import type { ReactNode } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { formatEuro, formatLongDate } from "../gulaConstants";
import { EXPENSE_CATEGORIES, labelFor } from "./accounting";
import {
  monthName,
  vatDeadline,
  type AccountingAlert,
  type AccountingSummary,
  type Period,
} from "./summary";

const Tile = ({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "good" | "bad";
}) => (
  <div className="rounded-lg border bg-card p-4">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div
      className={cn(
        "mt-1 text-2xl font-semibold tabular-nums",
        tone === "good" && "text-emerald-700 dark:text-emerald-400",
        tone === "bad" && "text-destructive",
      )}
    >
      {value}
    </div>
    {hint ? (
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    ) : null}
  </div>
);

const Row = ({
  label,
  base,
  amount,
  strong,
}: {
  label: string;
  base?: number;
  amount: number;
  strong?: boolean;
}) => (
  <tr className={cn("border-b last:border-0", strong && "font-semibold")}>
    <td className="py-1.5 pr-3">{label}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
      {base != null ? formatEuro(base) : ""}
    </td>
    <td className="py-1.5 text-right tabular-nums">
      {formatEuro(amount || 0)}
    </td>
  </tr>
);

/** Period overview: result, VAT return estimate, spending by category, months. */
export const AccountingSummaryView = ({
  summary,
  alerts,
  period,
  suppliers347,
  onReview,
}: {
  summary: AccountingSummary;
  alerts: AccountingAlert[];
  period: Period;
  suppliers347: { name: string; taxId: string; total: number }[];
  onReview: () => void;
}) => {
  const { vat } = summary;
  const maxCategory = Math.max(...summary.byCategory.map((row) => row.base), 1);
  const deadline = period.quarter
    ? vatDeadline(period.year, period.quarter)
    : null;

  return (
    <div className="flex flex-col gap-4">
      {alerts.length ? (
        <Card className="gap-2 border-amber-300 bg-amber-50 p-4 dark:border-amber-400/30 dark:bg-amber-400/10">
          <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            Falta para cerrar el periodo
          </div>
          <ul className="flex flex-col gap-1 text-sm text-amber-900 dark:text-amber-100">
            {alerts.map((alert) => (
              <li key={alert.key}>
                {alert.expenseIds ? (
                  <button
                    type="button"
                    onClick={onReview}
                    className="text-left underline-offset-2 hover:underline"
                  >
                    {alert.text}
                  </button>
                ) : (
                  alert.text
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Ingresos (sin IVA)"
          value={formatEuro(summary.incomeBase)}
          hint={`${formatEuro(summary.stripeBase)} de Stripe · ${formatEuro(summary.manualBase)} a mano`}
        />
        <Tile
          label="Gastos (sin IVA)"
          value={formatEuro(summary.expenseBase)}
          hint={`${summary.expenseCount} ${summary.expenseCount === 1 ? "factura" : "facturas"} · deducible ${formatEuro(summary.deductibleBase)}`}
        />
        <Tile
          label="Resultado"
          value={formatEuro(summary.result)}
          tone={summary.result >= 0 ? "good" : "bad"}
          hint="Ingresos menos gastos, antes de impuestos"
        />
        <Tile
          label={
            vat.balance >= 0 ? "IVA a pagar (estimado)" : "IVA a compensar"
          }
          value={formatEuro(Math.abs(vat.balance))}
          hint={
            deadline
              ? `Modelo 303 hasta el ${formatLongDate(deadline)}`
              : "Suma de los cuatro trimestres"
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="gap-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold">IVA del periodo</h2>
            {deadline ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" />
                presentar hasta el {formatLongDate(deadline)}
              </span>
            ) : null}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="pb-1 text-left font-normal">Concepto</th>
                <th className="pb-1 text-right font-normal">Base</th>
                <th className="pb-1 text-right font-normal">Cuota</th>
              </tr>
            </thead>
            <tbody>
              {vat.salesByRate.map((row) => (
                <Row
                  key={row.rate}
                  label={`IVA de las ventas (${row.rate} %)`}
                  base={row.base}
                  amount={row.vat}
                />
              ))}
              {vat.reverseVat ? (
                <Row
                  label="IVA de compras en la UE (Meta…), a declarar"
                  base={vat.reverseBase}
                  amount={vat.reverseVat}
                />
              ) : null}
              <Row label="IVA devengado" amount={vat.outputVat} strong />
              <Row
                label="IVA de compras y gastos en España"
                base={vat.domesticBase}
                amount={-vat.domesticVat}
              />
              {vat.reverseVat ? (
                <Row
                  label="IVA de compras en la UE, a deducir"
                  amount={-(vat.deductibleVat - vat.domesticVat)}
                />
              ) : null}
              <Row
                label={vat.balance >= 0 ? "A ingresar" : "A compensar"}
                amount={vat.balance}
                strong
              />
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground">
            Estimación con lo que hay en el CRM. El gestor revisa y presenta el
            modelo.
          </p>
        </Card>

        <Card className="gap-3 p-4">
          <h2 className="font-semibold">En qué se va el dinero</h2>
          {summary.byCategory.length ? (
            <ul className="flex flex-col gap-2">
              {summary.byCategory.map((row) => (
                <li key={row.category} className="flex flex-col gap-1">
                  <div className="flex justify-between gap-2 text-sm">
                    <span>
                      {labelFor(EXPENSE_CATEGORIES, row.category)}
                      <span className="text-muted-foreground">
                        {" "}
                        · {row.count}
                      </span>
                    </span>
                    <span className="tabular-nums">{formatEuro(row.base)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full bg-primary"
                      style={{ width: `${(row.base / maxCategory) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aún no hay gastos en este periodo. Añade las facturas de compra
              con «Añadir gasto».
            </p>
          )}
        </Card>
      </div>

      <Card className="gap-2 p-4">
        <h2 className="font-semibold">Mes a mes</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="pb-1 text-left font-normal">Mes</th>
                <th className="pb-1 text-right font-normal">Ingresos</th>
                <th className="pb-1 text-right font-normal">Gastos</th>
                <th className="pb-1 text-right font-normal">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {summary.byMonth.map((row) => (
                <tr key={row.month} className="border-b last:border-0">
                  <td className="py-1.5 capitalize">{monthName(row.month)}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatEuro(row.income)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatEuro(row.expenses)}
                  </td>
                  <td
                    className={cn(
                      "py-1.5 text-right font-medium tabular-nums",
                      row.result < 0 && "text-destructive",
                    )}
                  >
                    {formatEuro(row.result)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {period.quarter == null && suppliers347.length ? (
        <Card className="gap-2 p-4">
          <h2 className="font-semibold">
            Modelo 347: proveedores de más de 3.005,06 €
          </h2>
          <ul className="text-sm">
            {suppliers347.map((row) => (
              <li
                key={`${row.name}-${row.taxId}`}
                className="flex justify-between border-b py-1.5 last:border-0"
              >
                <span>
                  {row.name}
                  <span className="text-muted-foreground">
                    {row.taxId ? ` · ${row.taxId}` : " · sin NIF"}
                  </span>
                </span>
                <span className="tabular-nums">{formatEuro(row.total)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
};
